import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { dismissQuoteRequest, getActiveShipmentsWithLatestStatus } from '../api/adminApi';
import { formatNullableString, logError, toNullableLocaleDateTimeString } from '../utilities';
import ConfirmModal, { ButtonLoadingSpinner, ElementWithKey } from './ConfirmModal';
import GenericDataTable from './GenericDataTable';
import { AdminShipmentWithLatestStatusModel } from '../apiModels/adminShipmentWithLatestStatusModel';
import { DefaultNullStringValue } from '../appConstants';
import ShipmentCreateModal from './ShipmentCreateModal';
import { ShipmentStatusModel } from '../apiModels/shipmentStatusModel';
import { getStatusOptions } from '../api/shipmentUpdateApi';
import { useNavigate } from 'react-router-dom';

const pageSize = 10;

const TableRowFormatter = (props: { 
    shipment: AdminShipmentWithLatestStatusModel,
    isViewDetailsButtonEnabled: boolean,
    detailsPath: string,
}) => {
    const navigate = useNavigate();
    const shipment = props.shipment;
    const dateLastUpdated = shipment.latestStatus != null ? new Date(shipment.latestStatus.updateTime) : null;
    
    return (
        <tr key={shipment.trackingNumber}>
            <td>
                <button className="btn btn-sm btn-outline-primary" onClick={(e) => {
                    if (!props.isViewDetailsButtonEnabled) {
                        e.preventDefault();
                        return;
                    }
                    else {
                        navigate(`${props.detailsPath}/${shipment.trackingNumber}`);
                    }
                }}>
                    <i className="bi bi-search"></i>
                </button>
            </td>
            <td>{shipment.trackingNumber}</td>
            <td>{formatNullableString(shipment.memo)}</td>
            <td>{formatNullableString(shipment.latestStatus?.location)}</td>
            <td>{formatNullableString(shipment.latestStatus?.shipmentStatus)}</td>
            <td>{(dateLastUpdated == null ? DefaultNullStringValue : toNullableLocaleDateTimeString(dateLastUpdated))}</td>
        </tr>
    );
};

export default function ShipmentsListTable(props: {
    getShipmentList: (page: number, count: number) => Promise<AdminShipmentWithLatestStatusModel[]>,
    noShipmentsMessage: string,
    detailsPath: string,
    title: string;
    canEdit: boolean;
}) {
    const [ isDataTableLoading, setIsDataTableLoading ] = useState<boolean>(false);
    const [ isShipmentStatusesLoading, setIsShipmentStatusesLoading] = useState<boolean>(false);
    const [ shipments, setShipments ] = useState<AdminShipmentWithLatestStatusModel[]>([]);
    const [ selectedPage, setSelectedPage ] = useState<number>(0);
    const [ shipmentStatuses, setShipmentStatuses] = useState<ShipmentStatusModel[]>([]);
    const notifyUserOfError = (err: string) => toast(err, { type: 'error' });

    const [showCreateShipmentModal, setShowCreateShipmentModal] = useState<boolean>(false);

    const loadActiveShipments = async() => {
        setIsDataTableLoading(true);

        try {
            const s = await props.getShipmentList(selectedPage, pageSize);
            setShipments(s);
            setIsDataTableLoading(false);
        } 
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }
    
            logError(error);
            setIsDataTableLoading(false);
        }
    };

    const loadShipmentStatusOptions = async() => {
        setIsShipmentStatusesLoading(true);

        try {
            const s = await getStatusOptions();
            setShipmentStatuses(s);
            setIsShipmentStatusesLoading(false);
            
        } 
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }
    
            logError(error);
            setIsShipmentStatusesLoading(false);
        }
    };
    
    useEffect(() => {
        loadActiveShipments();
    }, [ selectedPage ]);

    useEffect(() => {
        loadShipmentStatusOptions();
    }, []);

    return (
        <>
            <div className='row'>
                <h3 className='col-7'>{props.title}</h3>
                
                <div className='col-2 text-end'>
                    {(shipments.length < pageSize && selectedPage === 0) || 
                        <div className='col-12 text-center'>
                            <button className='btn btn-default' hidden={selectedPage === 0} onClick={() => setSelectedPage(selectedPage - 1)} disabled={selectedPage === 0}>
                                <i className='bi bi-chevron-left fs-4 fw-bold'></i>
                            </button>
                            <span className='mx-2'>{selectedPage + 1}</span>
                            <button className='btn btn-default' hidden={shipments.length < pageSize} onClick={() => setSelectedPage(selectedPage + 1)} disabled={shipments.length < pageSize}>
                                <i className='bi bi-chevron-right fs-4 fw-bold'></i>
                            </button>
                        </div>
                    }
                </div>

                <div className='col-3 text-end'>
                    {props.canEdit &&
                    <button className='btn btn-primary' onClick={() => setShowCreateShipmentModal(true)}>
                        <i className='bi bi-plus-lg fw-bold'></i> Create
                    </button>}
                    <button className='btn btn-default' onClick={() => loadActiveShipments()}>
                        <i className='bi bi-arrow-repeat fs-4 fw-bold'></i>
                    </button>
                </div>
            </div>

            <GenericDataTable data={shipments} 
                dataFormatter={data => <>{data.map(d => <TableRowFormatter key={d.trackingNumber} detailsPath={props.detailsPath} shipment={d} isViewDetailsButtonEnabled={!isShipmentStatusesLoading} />)}</>}
                noDataMessage={props.noShipmentsMessage}
                columnCount={6}
                isLoading={isDataTableLoading}>
                <tr>
                    <td></td>
                    <td>Tracking Number</td>
                    <td>Memo</td>
                    <td>Latest Location</td>
                    <td>Latest Status</td>
                    <td>Last Updated</td>
                </tr>
            </GenericDataTable>
            
            <ToastContainer />
            <ShipmentCreateModal show={showCreateShipmentModal} 
                setShow={setShowCreateShipmentModal}
                callback={loadActiveShipments}/>
        </>
    );
}
